import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getTelegramContacts, resetUnreadCount } from '../../reducers/telegram/telegram-action';
import {
  changeIsUserSelected,
  setSelectedContact,
  updateContactFromSocket,
} from '../../reducers/telegram/telegram-slice';
import TelegramContactCard from './components/TelegramContactCard';
import { useSocket } from '../../hooks/useSocket';

const loadingSpinner = () => (
  <div className="h-full flex flex-column align-items-center justify-content-center">
    <i className="pi pi-spin pi-spinner" style={{ fontSize: '1rem' }}></i>
  </div>
);

const Chats = () => {
  const dispatch = useDispatch();
  const containerRef = useRef(null);
  const firstSelectDone = useRef(false);

  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(false);

  const {
    contacts,
    selectedBotConfigId,
    selectedContact,
    limit,
    hasMore,
    isUserSelected,
    agentSearchItem,
  } = useSelector((state) => state.telegram);

  let companyKey = JSON.parse(localStorage.getItem('companykey'));

  const shouldConnectSocket = companyKey?.id && selectedBotConfigId;

  const { socket } = useSocket(
    shouldConnectSocket ? companyKey.id : null,
    null,
    null,
    selectedBotConfigId,
  );

  useEffect(() => {
    if (!socket) return;
    const handleMessageContactUpdate = (data) => {
      console.log('✅ Socket connected Chats:', socket.id);
      console.log('📩 message_contact_update received:', data);
      dispatch(updateContactFromSocket(data));
    };

    socket.on('message_contact_update', handleMessageContactUpdate);

    // Clean up when component unmounts or socket changes
    return () => {
      socket.off('message_contact_update', handleMessageContactUpdate);
    };
  }, [socket]);

  //  Fetch contacts (initial & pagination)

  const fetchChats = useCallback(
    async (isInitial = true) => {
      if (!selectedBotConfigId) return;

      if (isInitial) {
        setPage(0);
        firstSelectDone.current = false;
      }

      setLoading(true);
      try {
        await dispatch(
          getTelegramContacts({
            botConfigId: selectedBotConfigId,
            page: isInitial ? 0 : page,
            limit,
            globalFilter: agentSearchItem,
          }),
        ).unwrap?.();
      } catch (error) {
        console.error('Failed to fetch telegram contacts', error);
      } finally {
        setLoading(false);
      }
    },
    [selectedBotConfigId, page, limit, agentSearchItem],
  );

  //  Initial fetch when bot changes

  useEffect(() => {
    fetchChats(true);
  }, [selectedBotConfigId, agentSearchItem]);

  //  Fetch more when page changes

  useEffect(() => {
    if (page > 0) {
      fetchChats(false);
    }
  }, [page]);

  //  Auto-select first contact

  useEffect(() => {
    if (contacts.length > 0 && !firstSelectDone.current) {
      const firstContact = contacts[0];

      dispatch(setSelectedContact(firstContact));
      dispatch(
        resetUnreadCount({
          botConfigId: selectedBotConfigId,
          chatId: firstContact.chatId,
        }),
      );

      firstSelectDone.current = true;
    }
  }, [contacts, selectedBotConfigId]);

  //  Scroll handler (infinite scroll)

  const handleScroll = () => {
    if (!containerRef.current || loading || !hasMore) return;

    const { scrollTop, scrollHeight, clientHeight } = containerRef.current;
    const tolerance = 1;

    if (scrollTop + clientHeight + tolerance >= scrollHeight) {
      setPage((prev) => prev + 1);
    }
  };

  //  Contact select

  const handleSelect = (contact) => {
    dispatch(changeIsUserSelected(true));
    dispatch(setSelectedContact(contact));
    dispatch(
      resetUnreadCount({
        botConfigId: selectedBotConfigId,
        chatId: contact.chatId,
      }),
    );
  };

  return (
    <div ref={containerRef} onScroll={handleScroll} className="chats flex-grow-1">
      {loading && loadingSpinner()}

      {contacts.length > 0
        ? contacts.map((contact, index) => (
            <div className={`userChat p-0 mb-0`} key={contact.chatId}>
              <TelegramContactCard
                key={contact.chatId}
                contact={contact}
                isActive={selectedContact?.chatId === contact.chatId}
                onClick={() => handleSelect(contact)}
              />
            </div>
          ))
        : !loading && (
            <div className="h-full flex align-items-center justify-content-center">
              <span>No chats found</span>
            </div>
          )}
    </div>
  );
};

export default Chats;
